import { useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingContext, M_StateActions, MTabsInpage, NavigateContext } from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import RouteConfig from './routeConfig';
import LaneManagement from './routeLaneManagement';
import LaneConfiguration from './routeLaneConfig';
import RoutePreview from './routePreview';
import { hasPermission } from 'src/components/utils/permissionsHelper';

// unified tabs styling 
import 'src/pages/tabsInpage.css';

export default function RoutePage({ roles, onCreateTab, toastMessages, handleTabClose }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const matches = useMediaQuery('(min-width:900px)');
    const M_State = useSelector((state) => state.StateKeyValue);
    const location = useLocation();
    const { renameTab } = useContext(NavigateContext);
    const selectedTab = M_State.find(obj => obj.key === 'selectedTab')?.value;

    const [menu, setMenu] = useState(1);

    //============================================================================================================
    // Dont fallback to a constant key, always use the current tab id
    //============================================================================================================
    const [id, setId] = useState(() =>
        M_State.find(obj => obj.key === 'selectedTab')?.value
    );

    const [refresh, setRefresh] = useState(false);

    //============================================================================================================
    // EFFECT
    // Rename tab on language change
    //============================================================================================================
    useEffect(() => {
        if (!selectedTab) return; // Guard: ensure selectedTab is defined
        renameTab(String(selectedTab), t('Routes'));
    }, [ selectedTab, t]);

    //============================================================================================================
    // helper: read per-tab state
    //============================================================================================================
    const getSessionStorageData = (sessionID) => {
        if (!sessionID) return; // Guard: ensure sessionID is defined
        ctx.setIsLoad(true); // Show loading indicator
        const session = M_State.find(obj => obj.key === sessionID)?.value; // Retrieve session data for the given tab ID
        if (session) { // If session data exists
            const m = session.find(x => x.key === 'menu')?.value; // Extract the 'menu' value
            if (m) setMenu(m); // Update state if 'menu' value is found
        }
        ctx.setIsLoad(false);
    };

    //============================================================================================================
    // helper: upsert a single key under the per-tab namespace (merge, do not replace)
    //============================================================================================================
    const upsertSession = (tabId, kv) => {
        // kv: { key: string, value: any }
        const current = M_State.find(o => o.key === tabId)?.value || [];
        const filtered = current.filter(e => e.key !== kv.key);
        dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] }));
    };

    //============================================================================================================
    // EFFECT
    // restore on route changes too to keep per-tab state consistent
    //============================================================================================================
    useEffect(() => {
        getSessionStorageData(id); // Load session data for the current tab ID
        setRefresh(r => !r); // Trigger a refresh to ensure the component updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);


    //============================================================================================================
    // EFFECT
    // persist only the keys we own to avoid overwriting children components state
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        upsertSession(id, { key: 'menu', value: menu }); //  merge, not replace
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, menu ]);

    //============================================================================================================
    // EFFECT
    // Keep per-tab namespace in sync with the currently selected tab id
    //============================================================================================================
    useEffect(() => {
        const newId = M_State.find(obj => obj.key === 'selectedTab')?.value; // Get the currently selected tab ID
        if (newId && newId !== id) {
            setId(newId); // Update local state with the new tab ID if it has changed
            getSessionStorageData(newId); // Load session data for the new tab ID
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    //============================================================================================================
    // Define allowed tabs based on permissions
    //============================================================================================================
    const allowedTabs = () => {
        const tabArray = [
            { id: 1, labelTab: t('RouteConfiguration'), iconTab: <i className="pi pi-wrench"></i> },
            { id: 4, labelTab: t('RoutePreview'), iconTab: <i className="pi pi-eye"></i> }
        ];

        if (hasPermission(roles, 'Locations', 'Update') || hasPermission(roles, 'Locations', 'Admin')) {
            tabArray.push(
                { id: 3, labelTab: t('LaneConfiguration'), iconTab: <i className="pi pi-arrow-right-arrow-left"></i> },
                { id: 2, labelTab: t('LaneManagement'), iconTab: <i className="pi pi-arrow-down-left-and-arrow-up-right-to-center"></i> },
            );
        }

        return tabArray;
    };

    return (
        // Backward-compat bridge + unified layout classes (visual only)
        <div className="tabsInpage divGlobalSettings">
            <div className="tabsInpage__title titleSettings" style={{ color: ctx.secondaryColor }}>{t('Routes')}</div>
            {/* Standard body split: sidebar (tabs) + content (panel) */}
            <div className="tabsInpage__body">
                {/* Sidebar column controlled by CSS (widths, separator, responsive) */}
                <div className="tabsInpage__sidebar">
                    <MTabsInpage // Define the tabs in the page
                        type={matches ? 'column' : 'tabs'}
                        renderTabs={allowedTabs()}
                        firstTabOpen={menu}
                        onGetTab={(obj) => setMenu(obj.id)}
                        fixedArea={false}
                    />
                </div>
                {/* Content area (right panel). Legacy class kept for bridge; no behavior change */}
                <div className="tabsInpage__content settingsPageMainDiv">
                    {menu === 1 && (
                        <RouteConfig // Route configuration component to dynamic Route table options
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            currentMenu={menu}
                        />
                    )}
                    {menu === 2 && (
                        <LaneManagement // Lane management component to manage lanes within the route
                            toastMessages={toastMessages}
                            onBack={() => setMenu(3)}
                        />
                    )}
                    {menu === 3 && (
                        <LaneConfiguration // Lane configuration component to configure lanes-routes relation and dynamic table options
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                            currentMenu={menu}
                        />
                    )}
                    {menu === 4 && (
                        <RoutePreview // Route preview component to preview the selected route by table
                            toastMessages={toastMessages}
                            onBack={() => setMenu(1)}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
